import type { Company, Contact, Deal } from '@paintscout/api'
import { Drawer, makeStyles, Button, Typography, useUser, useClientOptions } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import React, { useState } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import classNames from 'classnames'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { getTaskTemplateOptions, hasIntegrationInterface } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util'
import ContactIcon from './ContactIcon'

const SPACE_BETWEEN = 'space-between'

const useStyles = makeStyles<Theme, ContactBarProps>(
  (theme) => ({
    root: {},
    contactBar: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center',
      flexDirection: 'row',
      marginTop: theme.spacing(2),
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing(1)
    },
    bottomDrawer: {
      display: 'flex',
      justifyContent: 'flex-start',
      flexDirection: 'column'
    },
    bottomDrawerHeader: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center',
      flexDirection: 'row',
      padding: theme.spacing(2),
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    drawerCloseIcon: {
      '&:hover': {
        cursor: 'pointer',
        color: theme.palette.primary.main
      }
    },
    bottomDrawerContent: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      padding: theme.spacing(2)
    },
    bottomDrawerButtons: {
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center',
      flexDirection: 'column',
      flexWrap: 'wrap',
      width: '100%',
      // padding: theme.spacing(2),
      // make this transparent
      boxShadow: theme.shadows[3],
      borderRadius: theme.borderRadius.lg
    },
    drawerButton: {
      color: theme.palette.common.black,
      ...theme.typography.h3,
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      width: '100%',
      textTransform: 'none',
      borderRadius: 'unset',
      '&:hover': {
        border: `1px solid ${theme.paintscout.palette.brand.Neutral.P80}`
      }
    },
    drawerButtonLabel: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center'
    },
    disabledDrawerButton: {
      color: theme.paintscout.palette.brand.Neutral.P85
    }
  }),
  { name: 'ContactBar' }
)

export interface ContactBarProps {
  classes?: StyleClasses<typeof useStyles>
  item:
    | Contact
    | Company
    | (Deal & {
        address: string
        city: string
        province: string
        country: string
        postal: string
        phone_number: string
        email: string
      })
  type: string
  canCreateQuotes?: boolean
  canCreateInvoices?: boolean
  canCreateEvent?: boolean
  canCreatePayment?: boolean
  hideDisabledActions?: boolean
  handleNewClick: (vals: { type: string; key?: string; isInvoice?: boolean }) => void
  production?: boolean
}

function ContactBar(props: ContactBarProps) {
  const classes = useStyles(props)
  const { crm: crmFlag, productionFlag } = useFlags()

  const { options } = useClientOptions()
  const taskTemplateOptions = getTaskTemplateOptions({ options, excludeEmpty: true })

  const { isCrew, isSales, isViewer, isSubscribed, trialExpired, user } = useUser()
  const {
    item,
    handleNewClick,
    type,
    canCreateQuotes,
    canCreateInvoices,
    hideDisabledActions,
    production,
    canCreateEvent,
    canCreatePayment
  } = props
  const hasCompanyCam = crmFlag && hasIntegrationInterface({ options, integration: 'companycam' })
  const hasTaskTemplates = productionFlag && taskTemplateOptions.length > 0 && type === 'deal'
  const address = item?.address
  const phone = item?.phone_number
  const email = item?.email

  const userOwnsDeal =
    type === 'deal' && 'owner' in item && removeAuth0Prefix(item.owner?.id) === removeAuth0Prefix(user?.user_id)

  function handleClick(vals: { type: string; key?: string; isInvoice?: boolean }) {
    setTaskDrawerOpen(false)
    setFileDrawerOpen(false)
    setDrawerOpen(false)
    handleNewClick(vals)
  }

  // const preferredContactMethod = (item as Contact)?.preferred_contact_method ?? null
  const preferredContactMethod = (item as any)?.preferred_contact_method ?? null
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false)
  const [fileDrawerOpen, setFileDrawerOpen] = useState(false)

  const handleAddressClick = () => {
    // Handle address click
    const parts = [
      item?.address ?? '',
      item?.city ?? '',
      item?.province ?? '',
      item?.country ?? '',
      item?.postal ?? ''
    ].filter((part) => part.trim() !== '') // Remove empty strings
    const formattedString = parts.join(', ')
    openMap(formattedString)
  }
  function openMap(address) {
    const encodedAddress = encodeURIComponent(address)
    if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
      window.open(`maps://?q=${encodedAddress}`, '_blank')
    } else {
      window.open(`https://www.google.com/maps/search/${encodedAddress}`, '_blank')
    }
  }

  const handlePhoneClick = () => {
    const phoneNumber = phone.replace(/\D/g, '')
    const formattedNumber = `tel:${phoneNumber}`
    window.open(formattedNumber, '_blank')
  }

  const handleEmailClick = () => {
    const emailAddress = email.replace(/\s/g, '')
    const formattedEmail = `mailto:${emailAddress}`
    window.open(formattedEmail, '_blank')
  }

  const handleSmsClick = () => {
    const phoneNumber = phone.replace(/\D/g, '')
    const formattedNumber = `sms:${phoneNumber}`
    window.open(formattedNumber, '_blank')
  }

  const handleDrawerClick = () => {
    setDrawerOpen(true)
  }

  const handleToggleTaskDrawerOpen = ({ direction }: { direction: 'open' | 'close' }) => {
    setDrawerOpen(false)
    setTaskDrawerOpen(direction === 'open')
  }

  const handleToggleFileDrawerOpen = ({ direction }: { direction: 'open' | 'close' }) => {
    setDrawerOpen(false)
    setFileDrawerOpen(direction === 'open')
  }

  const FileSourceDrawer = () => {
    return (
      <Drawer
        classes={{ paper: classes.root }}
        open={fileDrawerOpen}
        anchor="bottom"
        variant="temporary"
        onClose={() => handleToggleFileDrawerOpen({ direction: 'close' })}
      >
        <div className={classes.bottomDrawer}>
          <div className={classes.bottomDrawerHeader}>
            <Typography variant="h3" color="inherit">
              Add File
            </Typography>
            <CloseIcon
              className={classes.drawerCloseIcon}
              onClick={() => {
                handleToggleFileDrawerOpen({ direction: 'close' })
              }}
            />
          </div>
          <div className={classes.bottomDrawerContent}>
            <div className={classes.bottomDrawerButtons}>
              <Button
                className={classes.drawerButton}
                variant="outlined"
                color="primary"
                onClick={() => handleClick({ type: 'file' })}
              >
                My Device
              </Button>
              <Button
                className={classes.drawerButton}
                variant="outlined"
                color="primary"
                onClick={() => handleClick({ type: 'file-companycam' })}
              >
                CompanyCam
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    )
  }

  const TaskTemplateDrawer = () => {
    return (
      <Drawer
        classes={{ paper: classes.root }}
        open={taskDrawerOpen}
        anchor="bottom"
        variant="temporary"
        onClose={() => handleToggleTaskDrawerOpen({ direction: 'close' })}
      >
        <div className={classes.bottomDrawer}>
          <div className={classes.bottomDrawerHeader}>
            <Typography variant="h3" color="inherit">
              Add Task
            </Typography>
            <CloseIcon
              className={classes.drawerCloseIcon}
              onClick={() => {
                handleToggleTaskDrawerOpen({ direction: 'close' })
              }}
            />
          </div>
          <div className={classes.bottomDrawerContent}>
            <div className={classes.bottomDrawerButtons}>
              <Button
                className={classes.drawerButton}
                variant="outlined"
                color="primary"
                onClick={() => handleClick({ type: 'task' })}
              >
                <div className={classes.drawerButtonLabel}>
                  <div>Single Task</div>
                  <Typography variant="caption" color="textSecondary">
                    Create a one-off task
                  </Typography>
                </div>
              </Button>
              {taskTemplateOptions.map((template) => (
                <Button
                  key={template.key}
                  className={classes.drawerButton}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleClick({ type: 'taskTemplate', key: template.key })}
                >
                  <div className={classes.drawerButtonLabel}>
                    <div>{template.label}</div>
                    <Typography variant="caption" color="textSecondary">
                      {`${template.order.length} task${template.order.length !== 1 ? 's' : ''}`}
                    </Typography>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </Drawer>
    )
  }

  return (
    <div className={classes.contactBar}>
      <ContactIcon
        type="address"
        disabled={!address}
        preferred={preferredContactMethod === 'address'}
        onClick={handleAddressClick}
      />
      <ContactIcon
        type="phone"
        preferred={preferredContactMethod === 'phone'}
        disabled={!phone}
        onClick={handlePhoneClick}
      />
      <ContactIcon
        type="email"
        preferred={preferredContactMethod === 'email'}
        disabled={!email}
        onClick={handleEmailClick}
      />
      <ContactIcon type="sms" preferred={preferredContactMethod === 'sms'} disabled={!phone} onClick={handleSmsClick} />
      {!isCrew && (!isSales || userOwnsDeal) && isSubscribed && !trialExpired && (
        <ContactIcon type="add" outlined onClick={handleDrawerClick} />
      )}
      {hasTaskTemplates && <TaskTemplateDrawer />}
      {hasCompanyCam && <FileSourceDrawer />}
      <Drawer
        classes={{ paper: classes.root }}
        open={drawerOpen}
        anchor="bottom"
        variant="temporary"
        onClose={() => setDrawerOpen(false)}
      >
        <div className={classNames(classes.bottomDrawer, drawerOpen ? 'tour-hide-checklist-launcher' : undefined)}>
          <div className={classes.bottomDrawerHeader}>
            <Typography variant="h3" color="inherit">
              Add New
            </Typography>
            <CloseIcon className={classes.drawerCloseIcon} onClick={() => setDrawerOpen(false)} />
          </div>
          <div className={classes.bottomDrawerContent}>
            <div className={classes.bottomDrawerButtons}>
              {type === 'company' && (
                <Button
                  className={classes.drawerButton}
                  style={{ borderRadius: '0.5rem 0.5rem 0rem 0rem' }}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleClick({ type: 'contact' })}
                >
                  Contact
                </Button>
              )}
              {crmFlag && !isCrew && (!production || (production && canCreateEvent)) && (
                <Button
                  className={classes.drawerButton}
                  style={{ borderRadius: '0.5rem 0.5rem 0rem 0rem' }}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleClick({ type: production ? 'productionEvent' : 'event' })}
                >
                  Event
                </Button>
              )}
              {type !== 'deal' && crmFlag && !isCrew && (
                <Button
                  className={classes.drawerButton}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleClick({ type: 'deal' })}
                >
                  Deal
                </Button>
              )}
              {hideDisabledActions && !canCreateQuotes ? (
                <></>
              ) : (
                <Button
                  className={classNames({
                    [classes.drawerButton]: true,
                    [classes.disabledDrawerButton]: !canCreateQuotes
                  })}
                  variant="outlined"
                  color="primary"
                  // disabled={!canCreateQuotes}
                  onClick={canCreateQuotes ? () => handleClick({ type: 'estimate', isInvoice: false }) : null}
                >
                  Estimate
                </Button>
              )}
              {hideDisabledActions && !canCreateInvoices ? (
                <></>
              ) : (
                <Button
                  className={classNames({
                    [classes.drawerButton]: true,
                    [classes.disabledDrawerButton]: !canCreateInvoices
                  })}
                  variant="outlined"
                  color="primary"
                  onClick={canCreateInvoices ? () => handleClick({ type: 'invoice', isInvoice: true }) : null}
                >
                  Invoice
                </Button>
              )}
              {type == 'deal' && crmFlag && !isCrew && canCreatePayment && (
                <Button
                  className={classes.drawerButton}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleClick({ type: 'payment' })}
                >
                  Payment
                </Button>
              )}
              {crmFlag && !isCrew && (
                <Button
                  className={classes.drawerButton}
                  variant="outlined"
                  color="primary"
                  onClick={
                    hasTaskTemplates
                      ? () => handleToggleTaskDrawerOpen({ direction: 'open' })
                      : () => handleClick({ type: 'task' })
                  }
                >
                  Task
                </Button>
              )}
              {crmFlag && !isCrew && !(isViewer && item.type === 'deal') && (
                <Button
                  className={classes.drawerButton}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleClick({ type: 'note' })}
                >
                  Note
                </Button>
              )}
              {crmFlag && !isCrew && !(isViewer && item.type === 'deal') && (
                <Button
                  className={classes.drawerButton}
                  variant="outlined"
                  color="primary"
                  onClick={
                    hasCompanyCam
                      ? () => handleToggleFileDrawerOpen({ direction: 'open' })
                      : () => handleClick({ type: 'file' })
                  }
                >
                  File
                </Button>
              )}
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  )
}

export default ContactBar
